import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import PageShell from "@/components/PageShell";
import PolicyConsentCheckbox from "@/components/PolicyConsentCheckbox";
import EmailNoticeBanner from "@/components/EmailNoticeBanner";
import PromoCodeInput from "@/components/PromoCodeInput";
import useCouponVisibility from "@/hooks/useCouponVisibility";
import useAutoDiscounts, { discountForService } from "@/hooks/useAutoDiscounts";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, CalendarDays, Clock } from "lucide-react";

const AR_WEEKDAYS = ["أحد", "اثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];

// Add N working days (skipping Fri=5 & Sat=6 in JS getDay).
const addWorkdays = (start, n) => {
  const d = new Date(start);
  d.setHours(0, 0, 0, 0);
  let added = 0;
  while (added < n) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 5 && dow !== 6) added += 1;
  }
  return d;
};
const AR_MONTHS = [
  "يناير", "فبراير", "مارس", "إبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];
// Custom Head that always renders Sun→Sat in DOM order with NO direction reversal.
const HeadAr = () => (
  <thead style={{ direction: "ltr" }} data-testid="custom-head">
    <tr style={{ display: "flex", flexDirection: "row", direction: "ltr" }}>
      {AR_WEEKDAYS.map((w) => (
        <th
          key={w}
          scope="col"
          className="text-muted-foreground rounded-md font-normal text-[0.8rem]"
          style={{ width: "2rem", textAlign: "center" }}
        >
          {w}
        </th>
      ))}
    </tr>
  </thead>
);
const calendarFormatters = {
  formatCaption: (d) => `${AR_MONTHS[d.getMonth()]} ${d.getFullYear()}`,
};
const calendarComponents = { Head: HeadAr };

// Display-only formatter — the underlying ISO is preserved on selection and
// sent to the backend as-is. We render the hour as a 12-hour clock with an
// Arabic AM/PM marker (صباحاً / مساءً) per the spec:
//   00:00 → 12:00 صباحاً        12:00 → 12:00 مساءً
//   09:00 → 09:00 صباحاً        14:00 → 02:00 مساءً
//   15:00 → 03:00 مساءً
function formatHour(iso) {
  const d = new Date(iso);
  const h24 = d.getHours();
  const isPm = h24 >= 12;
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  const suffix = isPm ? "مساءً" : "صباحاً";
  return `${String(h12).padStart(2, "0")}:00 ${suffix}`;
}

const Booking = () => {
  const { serviceId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { sessions: showSessionCoupon } = useCouponVisibility();
  const autoDiscounts = useAutoDiscounts();

  const [service, setService] = useState(null);
  const [date, setDate] = useState(undefined);
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fullDays, setFullDays] = useState(new Set()); // YYYY-MM-DD strings — capped days
  // Iteration 77 — dates where the slot grid has zero available slots
  // (admin-blocked, fully booked-by-buffer, or lead-time clipped). Greyed
  // out in the calendar so customers cannot click and then see an empty
  // slot list.
  const [emptyDays, setEmptyDays] = useState(new Set());
  const [policiesAccepted, setPoliciesAccepted] = useState(false);
  const [promo, setPromo] = useState(null);
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d;
  });
  // Iteration 96 — scheduled-call only: on initial load, open the calendar on
  // the first month that has a genuinely bookable day (backend decides via
  // /availability/first-available). Runs once per service; manual navigation
  // afterwards is never overridden.
  const initialJumpRef = useRef(null);
  const [jumpPending, setJumpPending] = useState(false);

  useEffect(() => {
    api.get(`/services/${serviceId}`).then((r) => setService(r.data)).catch(() => {
      toast.error("الخدمة غير متاحة");
      navigate("/services");
    });
  }, [serviceId, navigate]);

  useEffect(() => {
    if (!service || service.id !== "scheduled-call") return;
    if (initialJumpRef.current === service.id) return;
    initialJumpRef.current = service.id;
    setJumpPending(true);
    api
      .get("/availability/first-available", { params: { service_id: service.id } })
      .then((r) => {
        if (r.data?.found && r.data.date) {
          const first = new Date(`${r.data.date}T00:00:00`);
          const m = new Date(first); m.setDate(1);
          setViewMonth(m);
          setDate(first);
        }
      })
      .catch(() => {})
      .finally(() => setJumpPending(false));
  }, [service]);

  // Auto-select first available workday so the slot grid populates immediately.
  useEffect(() => {
    if (!service || date) return;
    if (service.id === "scheduled-call" && (jumpPending || initialJumpRef.current !== service.id)) return;
    const now = new Date();
    let start;
    const lead = service.lead || {};
    if (lead.kind === "immediate_24h") {
      start = new Date(now);
      start.setHours(0, 0, 0, 0);
    } else if (lead.kind === "workdays") {
      start = addWorkdays(now, lead.n);
    } else if (lead.kind === "workweeks") {
      start = addWorkdays(now, lead.n * 5);
    } else if (lead.kind === "calendar_days") {
      start = new Date(now);
      start.setDate(start.getDate() + lead.n);
    } else if (lead.kind === "earliest_date") {
      const ed = new Date(lead.date + "T10:00:00");
      const plus24 = new Date(now); plus24.setDate(plus24.getDate() + 1);
      start = ed > plus24 ? ed : plus24;
    } else {
      start = new Date(now); start.setDate(start.getDate() + 1);
    }
    start.setHours(0, 0, 0, 0);
    const ALLOWED = new Set(service.is_immediate ? [0, 1, 2, 3, 4, 5, 6] : [0, 1, 2, 3, 4]);
    for (let i = 0; i < 30; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      if (ALLOWED.has(d.getDay())) {
        setDate(d);
        break;
      }
    }
  }, [service, date]);

  const dateStr = useMemo(() => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [date]);

  // Iteration 63 — human-readable Arabic date for the final review box
  // (e.g. "الأربعاء، 5 يونيو 2026"). Mirrors the booking-summary chip the
  // backend stores so the customer sees exactly what's about to be booked.
  const dateLabel = useMemo(() => {
    if (!date) return "";
    const wd = AR_WEEKDAYS[date.getDay()];
    return `${wd}، ${date.getDate()} ${AR_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
  }, [date]);

  useEffect(() => {
    if (!dateStr || !serviceId) return;
    setLoadingSlots(true);
    setSelected(null);
    api
      .get("/availability", { params: { date_str: dateStr, service_id: serviceId } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => toast.error("تعذّر تحميل الأوقات"))
      .finally(() => setLoadingSlots(false));
  }, [dateStr, serviceId]);

  // Fetch full-days (daily-cap reached) for the visible month so we can grey
  // them out in the calendar. Skipped for جلسة فورية (cap-exempt).
  useEffect(() => {
    if (!serviceId || !service) return;
    if (service.is_immediate) {
      setFullDays(new Set());
      setEmptyDays(new Set());
      return;
    }
    const y = viewMonth.getFullYear();
    const m = viewMonth.getMonth() + 1;
    api
      .get("/availability/month", { params: { year: y, month: m, service_id: serviceId } })
      .then((r) => {
        setFullDays(new Set(r.data.full_days || []));
        setEmptyDays(new Set(r.data.empty_days || []));
      })
      .catch(() => {
        setFullDays(new Set());
        setEmptyDays(new Set());
      });
  }, [serviceId, service, viewMonth]);

  const handleConfirm = async () => {
    if (!user) {
      toast("يلزم تسجيل الدخول أولًا");
      navigate(`/login?next=/booking/${serviceId}`);
      return;
    }
    if (!selected) {
      toast.error("اختر وقتًا متاحًا");
      return;
    }
    if (!policiesAccepted) {
      toast.error("يرجى قراءة سياسة الخصوصية والاسترجاع والموافقة عليها قبل المتابعة");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout/create", {
        service_id: serviceId,
        slot_iso: selected,
        origin_url: window.location.origin,
        policies_accepted: true,
        ...(promo?.code ? { promo_code: promo.code } : {}),
      });
      // Iteration 24: a 100% discount coupon yields a coupon-bypass response
      // pointing directly at /checkout/success.
      if (data.provider === "coupon_bypass" && data.url) {
        window.location.href = data.url;
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "تعذّر بدء الدفع");
      setSubmitting(false);
    }
  };

  if (!service) return <PageShell><div className="p-12">جاري التحميل...</div></PageShell>;

  // Iteration 74 — If admin marked this service as fully booked, do not
  // render the calendar / slot grid / checkout at all. Show a clear Arabic
  // notice + a link back to the services page. Backend also rejects any
  // checkout POST with HTTP 409 as defence-in-depth.
  if (service.is_fully_booked) {
    return (
      <PageShell>
        <section
          className="max-w-3xl mx-auto px-6 lg:px-12 py-16"
          data-testid="booking-fully-booked-notice"
        >
          <button
            onClick={() => navigate("/services")}
            className="text-[#0A4D3C] inline-flex items-center gap-2 mb-6 hover:underline"
            data-testid="booking-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> العودة للخدمات
          </button>
          <div className="card-warm p-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FBEAEA] text-[#B33939] font-medium">
              محجوزة بالكامل
            </div>
            <h1 className="font-heading text-3xl text-[#222222] mt-4">
              {service.title_ar}
            </h1>
            <p className="text-[#5E5C58] mt-3 leading-relaxed">
              هذه الجلسة محجوزة بالكامل حاليًا، يرجى اختيار جلسة أخرى.
            </p>
            <Button
              onClick={() => navigate("/services")}
              className="mt-6 rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
              data-testid="booking-return-services-btn"
            >
              العودة إلى الجلسات
            </Button>
          </div>
        </section>
      </PageShell>
    );
  }

  // Disable past dates, before minimum lead time, and non-allowed weekdays.
  const disabledDates = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    const dow = d.getDay();
    if (service?.is_immediate) {
      // "جلسة فورية" — bookable from today on ANY day of the week, no upper cap.
      return false;
    }
    // Regular services: only Sun-Thu
    if (dow === 5 || dow === 6) return true;
    // Enforce min lead time per service
    const lead = service?.lead || {};
    let minDate = null;
    if (lead.kind === "workdays") minDate = addWorkdays(today, lead.n);
    else if (lead.kind === "workweeks") minDate = addWorkdays(today, lead.n * 5);
    else if (lead.kind === "calendar_days") {
      minDate = new Date(today);
      minDate.setDate(minDate.getDate() + lead.n);
    } else if (lead.kind === "earliest_date") {
      minDate = new Date(lead.date + "T00:00:00");
    }
    if (minDate) {
      minDate.setHours(0, 0, 0, 0);
      if (d < minDate) return true;
    }
    // Daily capacity cap (max 4 regular sessions per day).
    // 'جلسة فورية' bypasses this cap.
    if (!service?.is_immediate) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const key = `${y}-${m}-${day}`;
      if (fullDays.has(key)) return true;
      // Iteration 77 — also disable days that have zero usable slots even if
      // they haven't hit the daily cap (e.g. admin blocked every slot, or
      // the lead-time/min-dt rule pushed all slots into the next day).
      if (emptyDays.has(key)) return true;
    }
    return false;
  };

  return (
    <PageShell>
      <section className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
        <button
          onClick={() => navigate("/services")}
          className="text-[#0A4D3C] inline-flex items-center gap-2 mb-6 hover:underline"
          data-testid="booking-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> العودة للخدمات
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 card-warm p-8">
            <h1 className="font-heading text-3xl text-[#222222]">{service.title_ar}</h1>
            <p className="text-[#5E5C58] mt-1">{service.subtitle_ar}</p>
            <div
              data-testid="booking-consultant"
              className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F6F4EE] border border-[#E5E1D8]"
            >
              <span className="font-medium text-[#0A4D3C] font-heading text-base">أنا الخالدي</span>
            </div>
            {service.ui_note && (
              <div className="mt-4 text-sm text-[#5E5C58] bg-[#F6F4EE] border border-[#E5E1D8] rounded-xl p-3 leading-relaxed"
                   data-testid="booking-ui-note">
                {service.ui_note}
              </div>
            )}
            {service.disclaimer && (
              <div
                data-testid="booking-text-disclaimer"
                className="mt-3 text-xs text-[#5E5C58] bg-[#FAF8F2] border border-[#E5E1D8] rounded-xl p-3 leading-relaxed"
              >
                {service.disclaimer}
              </div>
            )}

            <div className="mt-8 grid md:grid-cols-2 gap-8">
              <div>
                <div className="text-sm text-[#5E5C58] mb-3 flex items-center gap-2">
                  <CalendarDays className="w-4 h-4" /> اختر التاريخ
                </div>
                <div
                  className="bg-[#F6F4EE] rounded-2xl p-2 inline-block"
                  style={{ direction: "ltr" }}
                >
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={disabledDates}
                    weekStartsOn={0}
                    showOutsideDays={true}
                    formatters={calendarFormatters}
                    components={calendarComponents}
                    month={viewMonth}
                    onMonthChange={setViewMonth}
                    classNames={{
                      row: "flex w-full mt-2 flex-row",
                    }}
                    dir="ltr"
                    data-testid="booking-calendar"
                  />
                </div>
              </div>
              <div>
                <div className="text-sm text-[#5E5C58] mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> الأوقات المتاحة
                </div>
                {!date && (
                  <p className="text-[#5E5C58] text-sm">يرجى اختيار التاريخ أولًا.</p>
                )}
                {date && loadingSlots && (
                  <p className="text-[#5E5C58] text-sm">جاري تحميل الأوقات...</p>
                )}
                {date && !loadingSlots && slots.length === 0 && (
                  <p className="text-[#B33939] text-sm" data-testid="booking-no-slots">
                    لا توجد أوقات متاحة في هذا اليوم.
                  </p>
                )}
                <div className="grid grid-cols-3 gap-2 mt-2" data-testid="booking-slots-grid">
                  {slots.map((iso) => (
                    <button
                      key={iso}
                      data-testid={`slot-${iso}`}
                      onClick={() => setSelected(iso)}
                      className={`py-2 px-3 rounded-xl border text-sm transition-colors ${
                        selected === iso
                          ? "bg-[#0A4D3C] text-white border-[#0A4D3C]"
                          : "border-[#E5E1D8] bg-white hover:bg-[#F6F4EE]"
                      }`}
                    >
                      {formatHour(iso)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <aside className="card-warm p-8 h-fit sticky top-24">
            <div className="text-sm text-[#5E5C58]">إجمالي الحجز</div>
            {(() => {
              const auto = discountForService(service, autoDiscounts);
              // Coupon-preview takes precedence over auto only when the
              // customer has entered a code; the backend blocks stacking
              // when auto applies, so `promo` here only exists on
              // non-eligible services.
              if (promo) {
                return (
                  <div className="font-heading text-4xl text-[#0A4D3C] mt-2 flex items-baseline gap-2">
                    <span className="text-2xl text-[#A8A29E] line-through" data-testid="booking-price-original">
                      {Number(service.price).toLocaleString("ar-SA")}
                    </span>
                    <span data-testid="booking-price-final">
                      {Number(promo.finalAmount).toLocaleString("ar-SA")}
                    </span>
                    <span className="text-base text-[#5E5C58]">ر.س</span>
                  </div>
                );
              }
              if (auto) {
                return (
                  <>
                    <div className="font-heading text-4xl text-[#0A4D3C] mt-2 flex items-baseline gap-2">
                      <span
                        className="text-2xl text-[#A8A29E] line-through"
                        data-testid="booking-price-original"
                      >
                        {Number(auto.base).toLocaleString("ar-SA")}
                      </span>
                      <span data-testid="booking-price-final">
                        {Number(auto.final).toLocaleString("ar-SA")}
                      </span>
                      <span className="text-base text-[#5E5C58]">ر.س</span>
                    </div>
                    <div
                      className="mt-1 inline-block rounded-full bg-[#0A4D3C]/10 text-[#0A4D3C] text-xs px-3 py-1"
                      data-testid="booking-auto-discount-badge"
                    >
                      خصم {auto.percentage}%
                    </div>
                  </>
                );
              }
              return (
                <div className="font-heading text-4xl text-[#0A4D3C] mt-2 flex items-baseline gap-2">
                  <span data-testid="booking-price-final">
                    {Number(service.price).toLocaleString("ar-SA")}
                  </span>
                  <span className="text-base text-[#5E5C58]">ر.س</span>
                </div>
              );
            })()}
            <ul className="mt-5 space-y-2 text-sm text-[#5E5C58]">
              <li>• مدة الجلسة: ساعة كاملة</li>
              <li>• تأكيد فوري بعد الدفع</li>
            </ul>
            {showSessionCoupon && (
              <div className="mt-5">
                <PromoCodeInput
                  serviceId={serviceId}
                  originalAmount={Number(service.price)}
                  onApplied={setPromo}
                  onCleared={() => setPromo(null)}
                />
              </div>
            )}
            <PolicyConsentCheckbox
              checked={policiesAccepted}
              onChange={setPoliciesAccepted}
              className="mt-6"
            />
            <EmailNoticeBanner className="mt-4" />
            {selected && (
              <div
                data-testid="booking-final-review"
                className="mt-4 rounded-2xl border border-[#0A4D3C]/25 bg-[#F6F4EE] p-4"
              >
                <div className="font-heading text-base text-[#0A4D3C]">مراجعة الموعد</div>
                <div className="text-sm text-[#222222] mt-1">لقد اخترتِ:</div>
                <ul className="mt-2 space-y-1 text-sm text-[#222222]">
                  <li>
                    <span className="text-[#5E5C58]">الجلسة: </span>
                    <span className="font-medium" data-testid="booking-review-service">
                      {service.title_ar}
                    </span>
                  </li>
                  <li>
                    <span className="text-[#5E5C58]">التاريخ: </span>
                    <span className="font-medium" data-testid="booking-review-date">
                      {dateLabel}
                    </span>
                  </li>
                  <li>
                    <span className="text-[#5E5C58]">الوقت: </span>
                    <span className="font-medium" data-testid="booking-review-time">
                      {formatHour(selected)}
                    </span>
                  </li>
                </ul>
                <div className="mt-3 text-xs text-[#B33939]">
                  يرجى التأكد من الموعد المختار قبل متابعة الدفع.
                </div>
              </div>
            )}
            <Button
              data-testid="booking-confirm-btn"
              onClick={handleConfirm}
              disabled={!selected || submitting || !policiesAccepted}
              className="mt-4 w-full rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white h-11"
            >
              {submitting
                ? "جاري التحويل..."
                : promo && promo.finalAmount === 0
                ? "تأكيد الحجز المجاني"
                : "متابعة الدفع"}
            </Button>
            {!user && (
              <p className="text-xs text-[#5E5C58] mt-3">
                ستحتاج لتسجيل الدخول قبل إتمام الدفع.
              </p>
            )}
            <div
              data-testid="booking-reschedule-disclaimer"
              className="mt-6 rounded-2xl bg-[#F6F4EE] border border-[#E5E1D8] p-4"
            >
              <div className="flex items-start gap-2">
                <span className="mt-0.5 inline-flex w-7 h-7 rounded-full bg-[#25D366]/15 items-center justify-center shrink-0">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#1ebe5b]" fill="currentColor"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.1.55 4.15 1.6 5.96L2 22l4.24-1.11a9.86 9.86 0 0 0 5.8 1.85c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm5.78 14.07c-.24.68-1.41 1.34-1.95 1.39-.5.05-1.13.07-1.83-.11-.42-.13-.97-.31-1.66-.6-2.93-1.27-4.84-4.22-4.99-4.42-.15-.2-1.18-1.57-1.18-2.99 0-1.42.74-2.12 1-2.41.27-.29.58-.36.78-.36.2 0 .4 0 .57.01.18.01.43-.07.67.51.24.58.83 2.01.9 2.16.07.15.12.32.02.51-.1.2-.15.32-.29.5-.15.18-.31.4-.44.54-.15.15-.3.31-.13.61.18.29.78 1.29 1.67 2.09 1.15 1.03 2.13 1.35 2.42 1.5.29.15.46.13.63-.07.18-.2.73-.85.92-1.14.18-.29.37-.24.62-.15.25.1 1.6.75 1.87.89.27.13.45.2.52.31.07.12.07.68-.17 1.36z"/></svg>
                </span>
                <p className="text-sm leading-relaxed text-[#222222]">
                  في حال واجهت أي مشكلة، يرجى التواصل مع خدمة العملاء عبر الواتساب.
                </p>
              </div>
              <a
                href="https://wa.me/966538278484?text=السلام%20عليكم%2C%20أرغب%20بإعادة%20جدولة%20موعد%20جلستي."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-whatsapp-link"
                className="mt-3 inline-flex items-center gap-2 text-[#1ebe5b] hover:text-[#159b48] text-sm font-medium"
              >
                <span>تواصل عبر الواتساب</span>
                <span dir="ltr">+966 53 827 8484</span>
              </a>
            </div>
          </aside>
        </div>
      </section>
    </PageShell>
  );
};

export default Booking;
